/**
 * R2 Storage Helpers
 *
 * Handles client-side EXIF data stripping, presigned URL fetching,
 * and uploading with progress monitoring for Cloudflare R2 storage.
 */

interface UploadUrlResponse {
  url: string;
  key: string;
  expiresAt: number;
  error?: string;
}

interface DownloadUrlResponse {
  url: string;
  key: string;
  expiresAt: number;
  error?: string;
}

/**
 * Fetch a short-lived presigned PUT URL from our Cloudflare API
 */
export async function getUploadUrl(
  token: string,
  filename: string,
  contentType: string,
  size: number
): Promise<{ url: string; key: string; error: string | null }> {
  try {
    const res = await fetch('/api/upload-url', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ filename, contentType, size }),
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      return { url: '', key: '', error: errData.error || `Server responded with ${res.status}` };
    }

    const data = (await res.json()) as UploadUrlResponse;
    return { url: data.url, key: data.key, error: null };
  } catch (err: any) {
    return { url: '', key: '', error: err.message || 'Network error fetching upload URL' };
  }
}

/**
 * Fetch a short-lived presigned GET URL from our Cloudflare API
 */
export async function getDownloadUrl(
  token: string,
  key: string
): Promise<{ url: string; error: string | null }> {
  try {
    const res = await fetch(`/api/download-url?key=${encodeURIComponent(key)}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!res.ok) {
      const errData = await res.json().catch(() => ({}));
      return { url: '', error: errData.error || `Server responded with ${res.status}` };
    }

    const data = (await res.json()) as DownloadUrlResponse;
    return { url: data.url, error: null };
  } catch (err: any) {
    return { url: '', error: err.message || 'Network error fetching download URL' };
  }
}

/**
 * Strips EXIF metadata from photos (JPEG/PNG/WEBP) client-side by re-drawing
 * the image onto a canvas. Also extracts width and height dimensions.
 */
export function stripExifAndGetDimensions(
  file: File
): Promise<{ blob: Blob; width?: number; height?: number }> {
  return new Promise((resolve) => {
    // Only process common image formats
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      resolve({ blob: file });
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve({ blob: file, width: img.width, height: img.height });
          return;
        }
        
        // Draw image onto canvas (this strips metadata completely)
        ctx.drawImage(img, 0, 0);
        
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve({ blob, width: img.width, height: img.height });
            } else {
              resolve({ blob: file, width: img.width, height: img.height });
            }
          },
          file.type,
          0.92 // 92% quality for excellent compression vs visual fidelity balance
        );
      } catch (err) {
        console.error('Failed to strip EXIF on canvas:', err);
        resolve({ blob: file, width: img.width, height: img.height });
      }
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ blob: file });
    };
    
    img.src = objectUrl;
  });
}

/**
 * Uploads a file to R2 via XMLHttpRequest to allow progress tracking and cancel/abort capabilities
 */
export function uploadFileWithProgress(
  blob: Blob,
  uploadUrl: string,
  contentType: string,
  onProgress: (progress: number) => void
): { xhr: XMLHttpRequest; promise: Promise<void> } {
  const xhr = new XMLHttpRequest();
  
  const promise = new Promise<void>((resolve, reject) => {
    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const percentComplete = Math.round((e.loaded / e.total) * 100);
        onProgress(percentComplete);
      }
    });
    
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status code ${xhr.status}`));
      }
    });
    
    xhr.addEventListener('error', () => {
      reject(new Error('Network error during file upload.'));
    });
    
    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted by user.'));
    });
    
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(blob);
  });
  
  return { xhr, promise };
}
